import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { BULB_BUSY_COVER_SERVICE } from '../angularjs.providers';

/**
 * Service responsible for managing account settings and related operations.
 * Provides methods to update account information, manage multi-factor authentication,
 * handle group memberships, subscription management, badge deletion, promo code application,
 * billing test information retrieval, family account management, resume configuration,
 * web search indexing, and phone number updates.
 */
@Injectable({ providedIn: 'root' })
export class AccountSettingsService {
  constructor(
    private http: HttpClient,
    @Inject('BulbAuthService') private auth: any,
    @Inject(BULB_BUSY_COVER_SERVICE) private busy: any,
  ) {
  }

  /**
   * Returns the unique identifier of the currently authenticated user.
   * @return {string} The account ID of the current user.
   */
  private accountId(): string {
    return this.auth.currentUser.id;
  }

  /**
   * Updates account information with the provided patch data
   * @param patch - The patch object containing account fields to be updated
   * @return An Observable that emits the response from the PATCH request
   */
  updateAccount(patch: any): Observable<any> {
    return this.http.patch(`accounts/${this.accountId()}`, patch);
  }

  /**
   * Enables multi-factor authentication or refreshes the existing MFA configuration for the current account.
   *
   * @return An Observable that emits the response from the MFA enable/refresh API call
   */
  enableOrRefreshMfa(): Observable<any> {
    return this.http.post(`accounts/${this.accountId()}/connections/multiFactorAuth`, {});
  }

  /**
   * Joins a user to a group using the provided access token.
   *
   * @param accessToken - The access token required to authenticate and authorize the user join request
   * @return An Observable that emits the response from the server after attempting to join the group
   */
  joinGroup(accessToken: string): Observable<any> {
    return this.http.post(`token/member/${accessToken}`, {});
  }

  /**
   * Unsubscribes the current user from the account.
   * @return An Observable that emits the response from the delete request.
   */
  unsubscribe(): Observable<any> {
    return this.http.delete(`accounts/${this.accountId()}/subscribe`);
  }

  /**
   * Reactivates a subscription for the current account.
   *
   * @return An Observable that emits the response from the reactivation API call.
   */
  reactivateSubscription(): Observable<any> {
    return this.http.post(`accounts/${this.accountId()}/reactivate`, {});
  }

  /**
   * Deletes badges associated with the current account.
   * @return An Observable that emits the response from the delete request.
   */
  deleteBadges(): Observable<any> {
    return this.http.delete(`accounts/${this.accountId()}/badges`);
  }

  /**
   * Applies a promo code to the user's account
   * @param code - The promo code to be applied
   * @return An observable that emits the response from the server after applying the promo code
   */
  applyPromoCode(code: string): Observable<any> {
    return this.http.post(`accounts/usePromoCode/${code}`, {});
  }

  /**
   * Loads billing test information for the current account.
   * @return An Observable that emits the billing test information response.
   */
  loadBillingTestInfo(): Observable<any> {
    return this.http.get(`accounts/${this.accountId()}/billingTestInfo`);
  }

  /**
   * Toggles family account membership for the current user.
   * @param action - The action to perform, typically 'add' or 'remove' family member
   * @return An Observable that emits the response from the PATCH request
   */
  toggleFamilyAccounts(action: string): Observable<any> {
    return this.http.patch(`accounts/${this.accountId()}/familyMembers/${action}`, {
      firstName: this.auth.currentUser.firstName,
      lastName: this.auth.currentUser.lastName,
    });
  }

  /**
   * Disconnects a family member from the account by removing either a parent or child relationship.
   *
   * @param action - Specifies the type of disconnection to perform, either 'removeParent' or 'removeChild'
   * @param familyMemberId - The unique identifier of the family member to be disconnected
   * @return An Observable that emits the response from the HTTP patch request
   */
  disconnectFamilyMember(action: 'removeParent' | 'removeChild', familyMemberId: string): Observable<any> {
    return this.http.patch(`accounts/${this.accountId()}/familyMembers/${action}`, { familyMemberId });
  }

  /**
   * Updates the resume status for the current account
   * @param action - The action to perform on the resume, either 'enable' or 'disable'
   * @return An Observable that emits the response from the HTTP patch request
   */
  updateResume(action: 'enable' | 'disable'): Observable<any> {
    return this.http.patch(`accounts/${this.accountId()}/resume/${action}`, {});
  }

  /**
   * Updates the web search indexing configuration for the current account.
   * @param enabled - A boolean value indicating whether web search indexing should be enabled or disabled
   * @return An Observable that emits the response from the HTTP patch request
   */
  updateWebSearchIndexing(enabled: boolean): Observable<any> {
    return this.http.patch(`accounts/${this.accountId()}/webSearchIndexing/${enabled}`, {});
  }

  /**
   * Updates the phone number for the current account
   * @param phoneNumber - The new phone number to set for the account
   * @return An Observable that emits the result of the account update operation
   */
  updatePhoneNumber(phoneNumber: string): Observable<any> {
    return this.updateAccount({ phoneNumber });
  }
}
